/**
 * useAsyncExport Hook
 *
 * Generic hook for handling async exports using the job-based API
 * Supports any export type (products, sales, customers, etc.)
 *
 * Flow:
 * 1. POST to /api/lightspeed/{export_type}/export/async - returns job_id
 * 2. Poll GET /api/lightspeed/{export_type}/export/status/{job_id} every 3 seconds
 * 3. When complete, download from /api/lightspeed/{export_type}/export/download/{job_id}
 *
 * Uses simple REST polling instead of SSE to avoid timeout issues with AWS App Runner (120s limit)
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { downloadFile } from '../services/api'

interface ProgressUpdate {
  timestamp: string
  message: string
  current_count?: number
  total_count?: number
  percentage?: number
}

interface AsyncExportState {
  loading: boolean
  error: string | null
  success: string | null
  warning: string | null
  progressMessage: string
  currentCount: number
  totalCount: number
  percentage: number | null
}

interface UseAsyncExportOptions {
  exportType: string
  onComplete?: (filename: string, totalRecords: number) => void
  onError?: (error: string) => void
  onProgress?: (update: ProgressUpdate) => void
}

interface StartExportParams {
  [key: string]: string | number | boolean | undefined
}

export function useAsyncExport(options: UseAsyncExportOptions) {
  const { exportType, onComplete, onError, onProgress } = options

  const [state, setState] = useState<AsyncExportState>({
    loading: false,
    error: null,
    success: null,
    warning: null,
    progressMessage: '',
    currentCount: 0,
    totalCount: 0,
    percentage: null
  })

  const pollingIntervalRef = useRef<number | null>(null)
  const jobIdRef = useRef<string | null>(null)
  const downloadingRef = useRef<boolean>(false)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      downloadingRef.current = false
    }
  }, [])

  const updateProgress = useCallback((update: ProgressUpdate) => {
    setState(prev => ({
      ...prev,
      progressMessage: update.message,
      currentCount: update.current_count ?? prev.currentCount,
      totalCount: update.total_count ?? prev.totalCount,
      percentage: update.percentage ?? prev.percentage
    }))

    if (onProgress) {
      onProgress(update)
    }
  }, [onProgress])

  const handleError = useCallback((errorMessage: string) => {
    setState(prev => ({
      ...prev,
      loading: false,
      error: errorMessage,
      progressMessage: ''
    }))

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    downloadingRef.current = false

    if (onError) {
      onError(errorMessage)
    }
  }, [onError])

  const downloadExport = useCallback(async (jobId: string) => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'
    const getApiBaseUrl = () => {
      return API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`
    }

    try {
      const downloadUrl = `${getApiBaseUrl()}/lightspeed/${exportType}/export/download/${jobId}`
      const response = await fetch(downloadUrl, {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`)
      }

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition')
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/)
      const filename = filenameMatch ? filenameMatch[1] : `export_${jobId}.xlsx`

      // Download the file
      const blob = await response.blob()
      downloadFile(blob, filename)

      return { filename, blob }
    } catch (error) {
      console.error('Download failed:', error)
      throw new Error(`Failed to download export: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }, [exportType])

  const watchProgress = useCallback((jobId: string) => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'
    const getApiBaseUrl = () => {
      return API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`
    }

    const statusUrl = `${getApiBaseUrl()}/lightspeed/${exportType}/export/status/${jobId}`

    // Poll status endpoint every 3 seconds
    const pollStatus = async () => {
      try {
        const response = await fetch(statusUrl, {
          credentials: 'include'
        })

        if (!response.ok) {
          if (response.status === 404) {
            // Job not found - this is fatal, stop polling
            handleError('Export job not found or expired')
            return
          } else if (response.status === 504 || response.status === 502) {
            // Gateway timeout or bad gateway - transient network issue
            // Don't show error to user, just log and continue polling
            console.log('Status check timed out, will retry on next poll')
            return
          } else {
            // Other errors are fatal
            handleError(`Failed to check status: ${response.statusText}`)
            return
          }
        }

        const data = await response.json()

        // Update progress if we have a latest message
        if (data.latest_message) {
          updateProgress({
            timestamp: new Date().toISOString(),
            message: data.latest_message,
            current_count: data.current_count,
            total_count: data.total_count,
            percentage: data.percentage
          })
        }

        // Check if completed
        if (data.status === 'completed') {
          // Prevent multiple downloads from race condition
          if (downloadingRef.current) {
            console.log('Download already in progress, skipping')
            return
          }
          downloadingRef.current = true

          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }

          setState(prev => ({
            ...prev,
            progressMessage: 'Download starting...'
          }))

          try {
            const { filename } = await downloadExport(jobId)

            setState(prev => ({
              ...prev,
              loading: false,
              success: `Export completed successfully! File: ${filename}`,
              progressMessage: '',
              currentCount: 0,
              totalCount: 0,
              percentage: null
            }))

            if (onComplete) {
              onComplete(filename, data.total_records || 0)
            }
          } catch (downloadError) {
            handleError(downloadError instanceof Error ? downloadError.message : 'Download failed')
          } finally {
            downloadingRef.current = false
          }

          return
        }

        // Check if failed
        if (data.status === 'failed') {
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }

          handleError(data.error || 'Export failed')
          return
        }

      } catch (error) {
        console.error('Polling error:', error)
        // Don't stop polling on network errors - they may be transient
        // Just log and continue polling
      }
    }

    // Initial poll
    pollStatus()

    // Set up polling interval (every 3 seconds)
    pollingIntervalRef.current = setInterval(pollStatus, 3000)

  }, [exportType, updateProgress, handleError, downloadExport, onComplete])

  const startExport = useCallback(async (params: StartExportParams) => {
    // Stop any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    // Reset download flag
    downloadingRef.current = false

    // Reset state
    setState({
      loading: true,
      error: null,
      success: null,
      warning: null,
      progressMessage: 'Starting export...',
      currentCount: 0,
      totalCount: 0,
      percentage: null
    })

    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'
    const getApiBaseUrl = () => {
      return API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`
    }

    try {
      // Step 1: Start the export job
      const startUrl = `${getApiBaseUrl()}/lightspeed/${exportType}/export/async`

      // Build FormData from params
      const formData = new FormData()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value))
        }
      })

      const response = await fetch(startUrl, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(errorData.detail || `Failed to start export: ${response.statusText}`)
      }

      const jobData = await response.json()
      const jobId = jobData.job_id

      if (!jobId) {
        throw new Error('No job ID returned from server')
      }

      jobIdRef.current = jobId

      setState(prev => ({
        ...prev,
        progressMessage: 'Export job started, polling for status updates...'
      }))

      // Step 2: Start polling for progress
      watchProgress(jobId)

    } catch (error) {
      console.error('Failed to start export:', error)
      handleError(error instanceof Error ? error.message : 'Failed to start export')
    }
  }, [exportType, watchProgress, handleError])

  const cancel = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    downloadingRef.current = false

    setState({
      loading: false,
      error: null,
      success: null,
      warning: null,
      progressMessage: '',
      currentCount: 0,
      totalCount: 0,
      percentage: null
    })

    jobIdRef.current = null
  }, [])

  return {
    ...state,
    startExport,
    cancel,
    jobId: jobIdRef.current
  }
}
